import React from 'react';
import { FiEdit2 } from 'react-icons/fi';

import { Solicitacao } from '@/models/Solicitacao';
import IconButton from '@/components/IconButton';
import Information from '@/components/Information';

import { ArtifactFormModel } from '@/features/artifacts/components/ArtifactForm/ArtifactForm.types';
import ArtifactForm from '@/features/artifacts/components/ArtifactForm';

import CardItem, { CardItemProps } from '../CardItem';
import { EditSection, EditedLabel, Footer } from './styles';
import { CardItemRenderProps } from '../CardItem/types';

// Overrideable CardItem props for Solicitacao context
type CardProps = Omit<CardItemProps, 'renderDetails' | 'variant' | 'onClick'>;

export interface CardSolicitacaoProps extends CardProps {
  solicitacao: Solicitacao;
  form?: ArtifactFormModel;
  canEdit?: boolean;
  canSelect?: boolean;
  onResourceEditStart?: () => void;
  onResourceEditCancel?: () => void;
  onSelect?: () => void;
  onResourceEdit?: (resource: fhir4.FhirResource) => void;
}

export type CardSolicitacaoMode = NonNullable<CardItemProps['mode']>;

export type CardSolicitacaoProxyProps = Omit<
  CardSolicitacaoProps,
  'title' | 'description' | 'form'
>;

const CardSolicitacao: React.FC<CardSolicitacaoProps> = ({
  // Display props
  title,
  description,
  mode: controlledMode,
  loading = false,
  state,
  disabledMode = 'none',
  children,
  // Data props
  solicitacao,
  form,
  // Feature flags
  canEdit = false,
  canSelect = true,
  canExpand = true,
  // Slot renders
  renderActions,
  // Event handlers
  onResourceEditStart,
  onResourceEditCancel,
  onSelect,
  onResourceEdit,
}) => {
  const [internalMode, setInternalMode] = React.useState<CardSolicitacaoMode>(
    'default',
  );

  const mode = React.useMemo<CardSolicitacaoMode>(() => {
    if (controlledMode) {
      return controlledMode;
    }

    return internalMode;
  }, [controlledMode, internalMode]);

  const handleModeChange = React.useCallback(
    (nextMode: CardSolicitacaoMode) => {
      if (!controlledMode) {
        setInternalMode(nextMode);
      }
    },
    [controlledMode],
  );

  const mandatory = React.useMemo(() => {
    if (solicitacao.solicitacao_obrigatoria) {
      return true;
    }
    return false;
  }, [solicitacao.solicitacao_obrigatoria]);

  const MandatoryAlert = React.useMemo(() => {
    if (mandatory) {
      const motivo =
        solicitacao.solicitacao_obrigatoria_motivo ||
        'A solicitação deste item é obrigatória conforme diretrizes da organização. Para uma decisão final, utilize sempre o prontuário eletrônico.';

      return <Information type="warning">{motivo}</Information>;
    }
    return null;
  }, [mandatory, solicitacao.solicitacao_obrigatoria_motivo]);

  const CardFooter = React.useMemo(() => {
    if (children || mandatory) {
      return (
        <Footer>
          {children}
          {MandatoryAlert}
        </Footer>
      );
    }

    return null;
  }, [children, mandatory, MandatoryAlert]);

  const variant = React.useMemo(() => {
    if (solicitacao.solicitacao_incluida) {
      return 'added';
    }
    return 'default';
  }, [solicitacao.solicitacao_incluida]);

  const resolvedDisabledMode = React.useMemo(() => {
    if (disabledMode === 'all') {
      return 'all';
    }

    if (mandatory) {
      return 'selection';
    }

    return disabledMode;
  }, [disabledMode, mandatory]);

  const handleItemSelect = React.useCallback(() => {
    if (onSelect) {
      onSelect();
    }
  }, [onSelect]);

  const Details = React.useCallback(() => {
    if (canEdit && form) {
      return (
        <ArtifactForm
          form={form}
          resource={solicitacao.pedido}
          loading={loading}
          disabled={loading}
          onResourceSubmit={edited => {
            if (onResourceEdit) {
              onResourceEdit(edited);
            }

            handleModeChange('default');
          }}
          onResourceSubmitCancel={() => {
            if (onResourceEditCancel) {
              onResourceEditCancel();
            }

            handleModeChange('default');
          }}
        />
      );
    }
    return null;
  }, [
    canEdit,
    form,
    handleModeChange,
    loading,
    onResourceEdit,
    onResourceEditCancel,
    solicitacao,
  ]);

  const EditButton = React.useMemo(() => {
    if (canEdit) {
      return (
        <IconButton
          icon={<FiEdit2 size={16} />}
          onClick={() => {
            if (onResourceEditStart) {
              onResourceEditStart();
            }

            handleModeChange('details');
          }}
        />
      );
    }
    return null;
  }, [canEdit, handleModeChange, onResourceEditStart]);

  const Actions = React.useCallback(
    (renderProps: CardItemRenderProps) => {
      return (
        <>
          <EditSection>
            {solicitacao.solicitacao_alterada && <EditedLabel />}
            {EditButton}
          </EditSection>
          {renderActions && renderActions(renderProps)}
        </>
      );
    },
    [EditButton, solicitacao.solicitacao_alterada, renderActions],
  );

  return (
    <CardItem
      title={title}
      description={description}
      mode={mode}
      loading={loading}
      variant={variant}
      state={mandatory ? 'marked' : state}
      disabledMode={resolvedDisabledMode}
      canSelect={canSelect}
      canExpand={canExpand}
      onClick={handleItemSelect}
      renderDetails={Details}
      renderActions={Actions}
    >
      {CardFooter}
    </CardItem>
  );
};

export default CardSolicitacao;
